import { useState, useRef, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plane, Building2, CalendarClock, AlertTriangle, Upload, ScanLine, Check, Circle, CheckCircle2,
  ArrowRight, ArrowLeft, Loader2, Sparkles, FileText, MailWarning,
} from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SERVICES, SLUG_MAP, PASSPORT_FIELDS } from "@/data/services";

const ICONS = { Plane, Building2, CalendarClock, AlertTriangle };
const STEP_LABELS = ["Service", "Passport", "Details", "Review"];

const toISO = (s) => {
  if (!s) return "";
  const m = String(s).match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (m) return `${m[3]}-${m[2]}-${m[1]}`;
  return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : "";
};
const fromISO = (s) => {
  const m = String(s).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : s;
};

function Field({ f, value, onChange, error }) {
  const base = `mt-1.5 w-full rounded-xl border px-4 py-3 outline-none transition-colors bg-white ${error ? "border-brand-red ring-1 ring-brand-red/30" : "border-slate-200 focus:border-brand-red"}`;
  return (
    <div>
      <label className="text-xs uppercase tracking-[0.12em] text-slate-500 font-bold">{f.label}{f.required && <span className="text-brand-red"> *</span>}</label>
      {f.type === "select" ? (
        <select value={value || ""} onChange={(e) => onChange(f.name, e.target.value)} className={base} data-testid={`field-${f.name}`}>
          <option value="">Select…</option>
          {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : f.type === "textarea" ? (
        <textarea rows={3} value={value || ""} onChange={(e) => onChange(f.name, e.target.value)} placeholder={f.placeholder} className={`${base} resize-none`} data-testid={`field-${f.name}`} />
      ) : f.type === "date" ? (
        <input type="date" value={toISO(value)} onChange={(e) => onChange(f.name, fromISO(e.target.value))} className={base} data-testid={`field-${f.name}`} />
      ) : (
        <input value={value || ""} onChange={(e) => onChange(f.name, e.target.value)} placeholder={f.placeholder} className={base} data-testid={`field-${f.name}`} />
      )}
      {error && <p className="mt-1 text-xs text-brand-red font-medium">{error}</p>}
    </div>
  );
}

function DocRow({ index, label, doc, onUpload, uploading }) {
  const ref = useRef(null);
  return (
    <div className="flex items-center justify-between gap-3 py-2.5 border-b border-slate-100 last:border-0" data-testid={`checklist-item-${index}`}>
      <div className="flex items-start gap-2.5 min-w-0">
        {doc ? <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" /> : <Circle className="w-5 h-5 text-slate-300 shrink-0 mt-0.5" />}
        <div className="min-w-0">
          <p className="text-sm text-slate-700">{label}</p>
          {doc && <p className="text-xs text-emerald-600 truncate max-w-[220px]">{doc.filename}</p>}
        </div>
      </div>
      <input ref={ref} type="file" className="hidden" accept="image/*,application/pdf" data-testid={`checklist-upload-input-${index}`}
        onChange={(e) => { const f = e.target.files?.[0]; if (f) onUpload(index, f); }} />
      <button type="button" onClick={() => ref.current?.click()} disabled={uploading === index} data-testid={`checklist-upload-btn-${index}`}
        className={`shrink-0 inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full border text-xs font-semibold transition-colors ${doc ? "border-emerald-200 text-emerald-700 bg-emerald-50" : "border-slate-200 text-brand-navy hover:border-brand-red"}`}>
        {uploading === index ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} {doc ? "Replace" : "Attach"}
      </button>
    </div>
  );
}

export default function Apply() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { user, refreshUser } = useAuth();
  const preset = slug ? SLUG_MAP[slug] : null;

  const [step, setStep] = useState(preset ? 1 : 0);
  const [service, setService] = useState(preset || null);
  const [form, setForm] = useState({});
  const [scanning, setScanning] = useState(false);
  const [scanned, setScanned] = useState(false);
  const [passportFileId, setPassportFileId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [docs, setDocs] = useState({});
  const [uploadingDoc, setUploadingDoc] = useState(null);
  const fileRef = useRef(null);

  const setField = (k, v) => {
    setForm((prev) => ({ ...prev, [k]: v }));
    setErrors((prev) => (prev[k] ? { ...prev, [k]: undefined } : prev));
  };
  const allFields = useMemo(() => service ? [...PASSPORT_FIELDS, ...service.fields] : [], [service]);

  const overstayInfo = useMemo(() => {
    if (service?.key !== "overstay_report" || !form.overstay_from) return null;
    const m = String(form.overstay_from).match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (!m) return null;
    const d = new Date(+m[3], +m[2] - 1, +m[1]);
    const days = Math.max(0, Math.floor((Date.now() - d.getTime()) / 86400000));
    return { days, fee: (days * 0.5).toFixed(2) };
  }, [service, form.overstay_from]);

  const allChecked = service ? service.requirements.every((_, i) => docs[i]) : false;

  const uploadDoc = async (index, file) => {
    setUploadingDoc(index);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDocs((p) => ({ ...p, [index]: { file_id: data.file_id, path: data.path, filename: data.filename } }));
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setUploadingDoc(null);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setScanning(true); setScanned(false);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/passport/extract", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPassportFileId(data.file_id);
      setForm((prev) => ({ ...prev, ...Object.fromEntries(Object.entries(data.fields).filter(([, v]) => v)) }));
      setScanned(true);
      toast.success("Passport read successfully", { description: "We've auto-filled your details. Please review them." });
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setScanning(false);
    }
  };

  const validateDetails = () => {
    const errs = {};
    for (const f of allFields) if (f.required && !form[f.name]) errs[f.name] = "This field is required";
    setErrors(errs);
    const keys = Object.keys(errs);
    if (keys.length) {
      const el = document.querySelector(`[data-testid="field-${keys[0]}"]`);
      if (el) { el.scrollIntoView({ behavior: "smooth", block: "center" }); el.focus?.(); }
      toast.error("Please complete the highlighted required fields.");
      return false;
    }
    return true;
  };

  const submit = async () => {
    if (!user?.is_verified) { toast.error("Please verify your email before submitting."); return; }
    if (!allChecked) { toast.error("Please confirm all required documents in the checklist."); return; }
    setSubmitting(true);
    try {
      const documents = service.requirements.map((r, i) => ({ label: r, ...(docs[i] || {}) }));
      const { data } = await api.post("/applications", {
        service_type: service.key, form_data: { ...form, documents }, passport_file_id: passportFileId,
      });
      toast.success("Application submitted!", { description: `Reference ${data.reference_no}. A confirmation email with your PDF is on the way.` });
      nav(`/applications/${data.id}`);
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setSubmitting(false);
    }
  };

  const resend = async () => {
    try { await api.post("/auth/resend-verification", { email: user.email }); toast.success("Verification email sent."); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const next = () => {
    if (step === 0 && !service) { toast.error("Please choose a service."); return; }
    if (step === 1 && !passportFileId) { toast.error("Please upload your passport bio page photo to continue."); return; }
    if (step === 2 && !validateDetails()) return;
    setStep((s) => Math.min(s + 1, 3));
  };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
      {!user?.is_verified && (
        <div className="mb-6 rounded-2xl border border-amber-200 bg-amber-50 p-4 flex items-start gap-3" data-testid="apply-verify-banner">
          <MailWarning className="w-5 h-5 text-brand-amber shrink-0 mt-0.5" />
          <div className="text-sm text-amber-800">
            <strong>Verify your email to submit.</strong> You can fill in the form now, but submission requires a verified account.
            <button onClick={resend} className="ml-2 font-semibold underline">Resend email</button>
          </div>
        </div>
      )}

      <h1 className="font-serif text-4xl text-brand-navy tracking-tight">New Application</h1>
      <p className="text-slate-500 mt-1">Complete the steps below. Fields marked * are required.</p>

      {/* Progress */}
      <div className="mt-8 flex items-center gap-2" data-testid="wizard-progress">
        {STEP_LABELS.map((l, i) => (
          <div key={l} className="flex-1">
            <div className={`h-1.5 rounded-full transition-colors ${i <= step ? "bg-brand-red" : "bg-slate-200"}`} />
            <span className={`mt-2 block text-xs font-semibold ${i <= step ? "text-brand-red" : "text-slate-400"}`}>{i + 1}. {l}</span>
          </div>
        ))}
      </div>

      <div className="mt-8 min-h-[320px]">
        <AnimatePresence mode="wait">
          <motion.div key={step} initial={{ x: 24, opacity: 0 }} animate={{ x: 0, opacity: 1 }} exit={{ x: -24, opacity: 0 }} transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}>
            {step === 0 && (
              <div className="grid sm:grid-cols-2 gap-4" data-testid="step-service">
                {SERVICES.map((s) => {
                  const Icon = ICONS[s.icon] || Plane;
                  const active = service?.key === s.key;
                  return (
                    <button key={s.key} onClick={() => setService(s)} data-testid={`wizard-service-${s.key}`}
                      className={`text-left rounded-2xl border-2 p-5 transition-all ${active ? "border-brand-red bg-brand-red/5 shadow-soft" : "border-slate-200 bg-white hover:border-slate-300"}`}>
                      <div className={`w-11 h-11 rounded-xl flex items-center justify-center mb-3 ${active ? "bg-brand-red text-white" : "bg-slate-100 text-brand-red"}`}><Icon className="w-5 h-5" /></div>
                      <h3 className="font-semibold text-brand-navy">{s.title}</h3>
                      <p className="text-sm text-slate-500 mt-1">{s.short}</p>
                    </button>
                  );
                })}
              </div>
            )}

            {step === 1 && (
              <div data-testid="step-passport">
                <div className="rounded-2xl border-2 border-dashed border-slate-300 bg-slate-50 p-8 text-center">
                  <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleUpload} data-testid="passport-file-input" />
                  <div className="w-16 h-16 rounded-2xl bg-brand-red/10 flex items-center justify-center mx-auto mb-4 text-brand-red">
                    {scanning ? <ScanLine className="w-8 h-8 animate-pulse" /> : scanned ? <Check className="w-8 h-8 text-emerald-600" /> : <Upload className="w-8 h-8" />}
                  </div>
                  <h3 className="font-semibold text-brand-navy text-lg">{scanning ? "Reading your passport…" : scanned ? "Passport details captured" : "Upload your passport bio page"}</h3>
                  <p className="text-sm text-slate-500 mt-1 max-w-sm mx-auto">
                    {scanning ? "Our AI is extracting your details." : "Upload a clear photo of your passport's photo page. We'll read it and auto-fill your form."}
                  </p>
                  <button onClick={() => fileRef.current?.click()} disabled={scanning} data-testid="passport-upload-btn"
                    className="mt-5 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-brand-red text-white font-semibold hover:bg-brand-reddark transition-colors disabled:opacity-60">
                    {scanning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} {scanned ? "Upload a different photo" : "Choose passport photo"}
                  </button>
                  <p className="mt-3 text-xs text-slate-400" data-testid="passport-required-note">
                    <span className="text-brand-red font-semibold">*</span> A passport bio page photo is required to continue with your application.
                  </p>
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-5" data-testid="step-details">
                <div>
                  <h3 className="font-semibold text-brand-navy mb-3 flex items-center gap-2"><FileText className="w-4 h-4 text-brand-red" /> Passport &amp; personal details</h3>
                  <div className="grid sm:grid-cols-2 gap-4">
                    {PASSPORT_FIELDS.map((f) => <Field key={f.name} f={f} value={form[f.name]} onChange={setField} error={errors[f.name]} />)}
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-brand-navy mb-3 mt-6 flex items-center gap-2">{service?.title} details</h3>
                  <div className="grid sm:grid-cols-2 gap-4">
                    {service?.fields.map((f) => <Field key={f.name} f={f} value={form[f.name]} onChange={setField} error={errors[f.name]} />)}
                  </div>
                  {overstayInfo && (
                    <div className="mt-4 rounded-2xl border-2 border-brand-red/30 bg-brand-red/5 p-5 flex items-center justify-between gap-4" data-testid="overstay-fee-panel">
                      <div>
                        <p className="text-xs uppercase tracking-widest text-brand-red font-bold">Overstay estimate</p>
                        <p className="text-sm text-slate-600 mt-1">Auto-calculated to today at USD 0.50 per day. Final penalty is set by Vietnamese authorities.</p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="font-serif text-3xl text-brand-navy leading-none" data-testid="overstay-days">{overstayInfo.days} <span className="text-base">days</span></p>
                        <p className="text-brand-red font-bold mt-1" data-testid="overstay-fee">Est. fee: ${overstayInfo.fee}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {step === 3 && (
              <div data-testid="step-review">
                <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
                  <div className="bg-brand-navy text-white px-6 py-4 flex items-center justify-between">
                    <span className="font-semibold">{service?.title}</span>
                    <span className="text-xs text-slate-300">Review before submitting</span>
                  </div>
                  <dl className="divide-y divide-slate-100">
                    {allFields.filter((f) => form[f.name]).map((f) => (
                      <div key={f.name} className="px-6 py-3 grid grid-cols-2 gap-4 text-sm">
                        <dt className="text-slate-500">{f.label}</dt>
                        <dd className="text-brand-navy font-medium">{form[f.name]}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
                <p className="mt-4 text-xs text-slate-400">By submitting, you confirm the information is accurate. Our consultants will review your application and prepare a quotation.</p>
                <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-5" data-testid="document-checklist">
                  <h3 className="font-semibold text-brand-navy">Required documents</h3>
                  <p className="text-sm text-slate-500 mt-1">Attach each required document to continue. All items must be uploaded before submitting.</p>
                  <div className="mt-3">
                    {service?.requirements.map((r, i) => (
                      <DocRow key={i} index={i} label={r} doc={docs[i]} onUpload={uploadDoc} uploading={uploadingDoc} />
                    ))}
                  </div>
                  {!allChecked && <p className="mt-3 text-xs text-brand-red font-medium">Attach all documents above to enable submission.</p>}
                </div>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="mt-8 flex items-center justify-between">
        <button onClick={back} disabled={step === 0} data-testid="wizard-back-btn"
          className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-slate-600 font-semibold hover:bg-slate-100 transition-colors disabled:opacity-40">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        {step < 3 ? (
          <button onClick={next} data-testid="wizard-next-btn"
            className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-brand-red text-white font-semibold hover:bg-brand-reddark transition-colors shadow-redglow">
            Continue <ArrowRight className="w-4 h-4" />
          </button>
        ) : (
          <button onClick={submit} disabled={submitting || !allChecked} data-testid="wizard-submit-btn"
            className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-brand-red text-white font-semibold hover:bg-brand-reddark transition-colors shadow-redglow disabled:opacity-60">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Submit application
          </button>
        )}
      </div>
    </div>
  );
}
